import CropOriginalIcon from "@mui/icons-material/CropOriginal";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
    Box,
    Button,
    Card,
    CardActionArea,
    CardActions,
    CardContent,
    CardMedia,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Pagination,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "../components/ConfirmDialog";
import CustomSnackbar from "../components/CustomSnackbar";
import Item from "../components/Item";
import AdResourceService from "./AdResourceService";
import {
    ADMIN_ADVERTISEMENT_IPHONE_BACKGROUND_PATH,
    advertisementGeneratePath,
    advertisementResourceDetailPath,
} from "./advertisementRoutes";
import {
    downloadFileNameForAdResource,
    extensionForAdResource,
    sanitizeDownloadFileName,
} from "./adDownloadFileName";
import {
    AD_GENERATE_RESOURCE_TYPES,
    getAdResourceMediaUrl,
    getAdResourcePreviewUrl,
    getAdResourceVideoUrl,
    isSquareResourcePreview,
    isVideoAdResource,
} from "./adResourceTypes.config";

const PAGE_SIZE = 15;
const SORT = "createdAt:desc";
const RESOURCE_TYPE_FILTER_KEY = "admin.advertisement.resourceTypeFilter";

function readStoredResourceTypeFilter() {
    try {
        const stored = localStorage.getItem(RESOURCE_TYPE_FILTER_KEY);
        if (stored == null || stored === "") return "";
        const known = AD_GENERATE_RESOURCE_TYPES.some((entry) => entry.value === stored);
        return known ? stored : "";
    } catch {
        return "";
    }
}

function writeStoredResourceTypeFilter(value) {
    try {
        localStorage.setItem(RESOURCE_TYPE_FILTER_KEY, value);
    } catch {
        /* ignore quota / private mode */
    }
}

function formatEpoch(ms) {
    const n = Number(ms);
    if (!Number.isFinite(n) || n <= 0) return "—";
    return new Date(n).toLocaleString();
}

function formatDimensions(width, height) {
    const w = Number(width);
    const h = Number(height);
    if (Number.isFinite(w) && w > 0 && Number.isFinite(h) && h > 0) {
        return `${w}×${h}`;
    }
    return "—";
}

function formatSizeMb(sizeBytes) {
    const bytes = Number(sizeBytes);
    if (!Number.isFinite(bytes) || bytes <= 0) {
        return "—";
    }
    if (bytes < 1024) {
        return `${Math.round(bytes)} B`;
    }
    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatFileFormatLabel(row, isVideo) {
    const mime = String(row?.contentType ?? row?.content_type ?? "").trim();
    if (mime) {
        const subtype = mime.includes("/") ? mime.split("/").pop() : mime;
        const pretty = String(subtype || "")
            .replace(/^x-/, "")
            .toUpperCase();
        return pretty || mime;
    }
    const ext = extensionForAdResource(row, isVideo).replace(/^\./, "");
    return ext ? ext.toUpperCase() : "—";
}

function mediaAspectForRow(row) {
    if (isSquareResourcePreview(row)) return "1 / 1";
    const w = Number(row?.width);
    const h = Number(row?.height);
    if (Number.isFinite(w) && Number.isFinite(h) && h > w) return "9 / 16";
    return "16 / 9";
}

export default function AdvertisementMain() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [resourceTypeFilter, setResourceTypeFilter] = useState(readStoredResourceTypeFilter);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [acceptedSavingIds, setAcceptedSavingIds] = useState(() => new Set());
    const [downloadingIds, setDownloadingIds] = useState(() => new Set());
    const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
    const [downloadTarget, setDownloadTarget] = useState(null);
    const [downloadFileName, setDownloadFileName] = useState("");
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });

    const loadList = useCallback(async () => {
        setLoading(true);
        const result = await AdResourceService.list(page - 1, PAGE_SIZE, SORT, {
            resourceType: resourceTypeFilter,
        });
        setLoading(false);
        if (!result.success) {
            setRows([]);
            setTotalPages(0);
            setSnackbar({
                open: true,
                severity: "error",
                message: result.error || "Failed to load ad resources",
            });
            return;
        }
        setRows(result.data?.content ?? []);
        setTotalPages(result.data?.totalPages ?? 0);
    }, [page, resourceTypeFilter]);

    useEffect(() => {
        loadList();
    }, [loadList]);

    const handleResourceTypeFilterChange = (event) => {
        const next = String(event.target.value ?? "");
        setResourceTypeFilter(next);
        setPage(1);
        writeStoredResourceTypeFilter(next);
    };

    const handleConfirmDelete = async (targetId) => {
        if (!targetId) return;
        const result = await AdResourceService.delete(targetId);
        setDeleteTarget(null);
        if (!result.success) {
            setSnackbar({
                open: true,
                severity: "error",
                message: result.error || "Delete failed",
            });
            return;
        }
        setSnackbar({ open: true, severity: "success", message: "Ad resource deleted" });
        loadList();
    };

    const handleAcceptedChange = async (row, checked) => {
        if (!row?.id || acceptedSavingIds.has(row.id)) {
            return;
        }
        const previous = Boolean(row.accepted);
        setRows((current) =>
            current.map((item) =>
                item.id === row.id ? { ...item, accepted: checked } : item
            )
        );
        setAcceptedSavingIds((current) => new Set(current).add(row.id));
        const result = await AdResourceService.update(row.id, { accepted: checked });
        setAcceptedSavingIds((current) => {
            const next = new Set(current);
            next.delete(row.id);
            return next;
        });
        if (!result.success) {
            setRows((current) =>
                current.map((item) =>
                    item.id === row.id ? { ...item, accepted: previous } : item
                )
            );
            setSnackbar({
                open: true,
                severity: "error",
                message: result.error || "Failed to update accepted",
            });
            return;
        }
        if (result.data) {
            setRows((current) =>
                current.map((item) =>
                    item.id === row.id ? { ...item, ...result.data } : item
                )
            );
        }
    };

    const openDownloadDialog = (row) => {
        if (!row?.id || downloadingIds.has(row.id)) return;
        const downloadUrl = getAdResourceMediaUrl(row);
        if (!downloadUrl) return;
        const isVideo = isVideoAdResource(row);
        setDownloadTarget(row);
        setDownloadFileName(downloadFileNameForAdResource(row, isVideo));
        setDownloadDialogOpen(true);
    };

    const closeDownloadDialog = () => {
        if (downloadTarget?.id && downloadingIds.has(downloadTarget.id)) return;
        setDownloadDialogOpen(false);
        setDownloadTarget(null);
        setDownloadFileName("");
    };

    const handleDownloadMedia = async () => {
        const row = downloadTarget;
        if (!row?.id || downloadingIds.has(row.id)) return;
        const isVideo = isVideoAdResource(row);
        const downloadUrl = getAdResourceMediaUrl(row);
        if (!downloadUrl) return;

        const defaultName = downloadFileNameForAdResource(row, isVideo);
        const fileName = sanitizeDownloadFileName(downloadFileName, defaultName);

        setDownloadingIds((current) => new Set(current).add(row.id));
        try {
            const response = await fetch(downloadUrl);
            if (!response.ok) {
                throw new Error("Download failed");
            }
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = objectUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(objectUrl);
            setDownloadDialogOpen(false);
            setDownloadTarget(null);
            setDownloadFileName("");
        } catch (downloadError) {
            setSnackbar({
                open: true,
                severity: "error",
                message: downloadError?.message || "Download failed",
            });
        } finally {
            setDownloadingIds((current) => {
                const next = new Set(current);
                next.delete(row.id);
                return next;
            });
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Item sx={{ textAlign: "left" }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}
                    sx={{ mb: 1, flexWrap: "wrap", rowGap: 1 }}
                >
                    <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
                        Resources
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<CropOriginalIcon />}
                            onClick={() => navigate(ADMIN_ADVERTISEMENT_IPHONE_BACKGROUND_PATH)}
                            sx={{ textTransform: "none" }}
                        >
                            From standalone image
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<ImageOutlinedIcon />}
                            onClick={() => navigate(advertisementGeneratePath(resourceTypeFilter))}
                            sx={{ textTransform: "none" }}
                        >
                            Generate Media
                        </Button>
                    </Stack>
                </Stack>

                <Stack direction="row" justifyContent="flex-start" sx={{ mb: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 280 }}>
                        <InputLabel id="ad-resource-type-filter-label">Resource type</InputLabel>
                        <Select
                            labelId="ad-resource-type-filter-label"
                            label="Resource type"
                            value={resourceTypeFilter}
                            onChange={handleResourceTypeFilterChange}
                        >
                            <MenuItem value="">All types</MenuItem>
                            {AD_GENERATE_RESOURCE_TYPES.map((entry) => (
                                <MenuItem key={entry.value} value={entry.value}>
                                    {entry.value}
                                </MenuItem>
                            ))}
                            <MenuItem value="PC_BACKGROUND">PC_BACKGROUND</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : rows.length === 0 ? (
                    <Box sx={{ py: 6, textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                            No ad resources found.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Grid container spacing={2}>
                            {rows.map((row) => {
                                const previewUrl = getAdResourcePreviewUrl(row);
                                const isVideo = isVideoAdResource(row);
                                const openUrl = isVideo
                                    ? getAdResourceVideoUrl(row) ?? previewUrl
                                    : previewUrl;
                                const detailPath = row.id
                                    ? advertisementResourceDetailPath(row.id)
                                    : null;
                                const dimensionsLabel = formatDimensions(row.width, row.height);
                                const formatLabel = formatFileFormatLabel(row, isVideo);
                                const fileSizeLabel = formatSizeMb(row.sizeBytes ?? row.size_bytes);

                                return (
                                    <Grid
                                        key={row.id}
                                        size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                                    >
                                        <Card
                                            variant="outlined"
                                            sx={{
                                                height: "100%",
                                                display: "flex",
                                                flexDirection: "column",
                                                overflow: "hidden",
                                            }}
                                        >
                                            <CardActionArea
                                                disabled={!detailPath}
                                                onClick={() => detailPath && navigate(detailPath)}
                                                sx={{
                                                    flex: 1,
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "stretch",
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        position: "relative",
                                                        width: "100%",
                                                        aspectRatio: mediaAspectForRow(row),
                                                        maxHeight: 220,
                                                        bgcolor: "action.hover",
                                                        overflow: "hidden",
                                                    }}
                                                >
                                                    {previewUrl ? (
                                                        <CardMedia
                                                            component="img"
                                                            image={previewUrl}
                                                            alt={row.resourceType || "Ad resource"}
                                                            referrerPolicy="no-referrer"
                                                            sx={{
                                                                width: "100%",
                                                                height: "100%",
                                                                objectFit: "contain",
                                                                bgcolor: "grey.900",
                                                            }}
                                                        />
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                height: "100%",
                                                                minHeight: 120,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                            >
                                                                No preview
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    {isVideo ? (
                                                        <Box
                                                            sx={{
                                                                position: "absolute",
                                                                top: 8,
                                                                left: 8,
                                                                px: 1,
                                                                py: 0.25,
                                                                borderRadius: 1,
                                                                bgcolor: "rgba(0,0,0,0.65)",
                                                                color: "#fff",
                                                                typography: "caption",
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            VIDEO
                                                        </Box>
                                                    ) : null}
                                                </Box>
                                                <CardContent sx={{ py: 1.5, flexGrow: 1 }}>
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            fontWeight: 700,
                                                            lineHeight: 1.3,
                                                            wordBreak: "break-word",
                                                        }}
                                                    >
                                                        {row.resourceType ?? "—"}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ mt: 0.5 }}
                                                    >
                                                        {dimensionsLabel}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ mt: 0.25 }}
                                                    >
                                                        {formatLabel}
                                                        {" · "}
                                                        {fileSizeLabel}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ display: "block", mt: 0.75 }}
                                                    >
                                                        {formatEpoch(row.createdAt)}
                                                    </Typography>
                                                </CardContent>
                                            </CardActionArea>
                                            <CardActions
                                                sx={{
                                                    px: 1.5,
                                                    pb: 1.25,
                                                    pt: 0,
                                                    justifyContent: "space-between",
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={Boolean(row.accepted)}
                                                            disabled={acceptedSavingIds.has(row.id)}
                                                            onChange={(e) =>
                                                                handleAcceptedChange(
                                                                    row,
                                                                    e.target.checked
                                                                )
                                                            }
                                                            size="small"
                                                        />
                                                    }
                                                    label={
                                                        <Typography variant="caption">
                                                            Accepted
                                                        </Typography>
                                                    }
                                                    sx={{ mr: 0, ml: 0 }}
                                                />
                                                <Stack direction="row" spacing={0.25}>
                                                    {openUrl ? (
                                                        <Tooltip
                                                            title={isVideo ? "Open video" : "Open image"}
                                                        >
                                                            <IconButton
                                                                size="small"
                                                                component="a"
                                                                href={openUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                <OpenInNewIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : null}
                                                    {openUrl ? (
                                                        <Tooltip title="Download">
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    disabled={downloadingIds.has(row.id)}
                                                                    onClick={() => openDownloadDialog(row)}
                                                                >
                                                                    {downloadingIds.has(row.id) ? (
                                                                        <CircularProgress size={16} />
                                                                    ) : (
                                                                        <DownloadIcon fontSize="small" />
                                                                    )}
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    ) : null}
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            size="small"
                                                            color="warning"
                                                            onClick={() => {
                                                                setDeleteTarget(row);
                                                                setDeleteOpen(true);
                                                            }}
                                                        >
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </CardActions>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                        {totalPages > 1 ? (
                            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={(_, value) => setPage(value)}
                                    color="primary"
                                />
                            </Box>
                        ) : null}
                    </>
                )}
            </Item>

            <ConfirmDialog
                open={deleteOpen}
                setOpen={setDeleteOpen}
                title="Delete ad resource?"
                message="Removes the ad_resource row only. The S3 object is not deleted automatically. You cannot delete the last resource of a type."
                targetName={deleteTarget?.resourceType}
                targetId={deleteTarget?.id}
                callback={handleConfirmDelete}
            />

            <Dialog
                open={downloadDialogOpen}
                onClose={closeDownloadDialog}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Download as</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        margin="dense"
                        label="File name"
                        value={downloadFileName}
                        onChange={(e) => setDownloadFileName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                handleDownloadMedia();
                            }
                        }}
                        disabled={Boolean(
                            downloadTarget?.id && downloadingIds.has(downloadTarget.id)
                        )}
                        helperText="Change the name before saving. Extension is kept if omitted."
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={closeDownloadDialog}
                        disabled={Boolean(
                            downloadTarget?.id && downloadingIds.has(downloadTarget.id)
                        )}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleDownloadMedia}
                        disabled={
                            !downloadFileName.trim() ||
                            Boolean(
                                downloadTarget?.id && downloadingIds.has(downloadTarget.id)
                            )
                        }
                        startIcon={
                            downloadTarget?.id && downloadingIds.has(downloadTarget.id) ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <DownloadIcon />
                            )
                        }
                    >
                        Download
                    </Button>
                </DialogActions>
            </Dialog>

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(v) => setSnackbar((s) => ({ ...s, open: v }))}
                message={snackbar.message}
                severity={snackbar.severity}
            />
        </Box>
    );
}
